/**
 * Wire types mirroring ai.libx.js (OpenAI-style chat). We type the transport
 * structurally via `ChatLike`, so an ai.libx.js `AIClient` is a drop-in — and a
 * `FakeAIClient` works in tests — with no hard runtime dependency on ai.libx.js.
 */

import { readFileSync, statSync, realpathSync } from 'node:fs';
import { resolve, sep } from 'node:path';
import { randomBytes } from 'node:crypto';
import { forComponent } from './logging';

const log = forComponent('llm');

export type Role = 'system' | 'user' | 'assistant' | 'tool';

export interface ToolCall {
  id: string;
  type: 'function';
  function: { name: string; arguments: string }; // arguments is a JSON string
}

/** One part of a multimodal message (mirrors ai.libx.js ContentPart) — text or an image URL/data-URI. */
export interface ContentPart {
  type: 'text' | 'image_url';
  text?: string;
  image_url?: { url: string };
}

/** A message's content is either plain text or an array of multimodal parts (images + text). */
export type MessageContent = string | ContentPart[];

export interface Message {
  role: Role;
  content: MessageContent;
  name?: string;
  tool_call_id?: string;
  tool_calls?: ToolCall[];
  /** The assistant step's reasoning, kept ONLY for providers that need it passed back (see passesBackReasoning). */
  reasoning_content?: string;
  /** Concrete model this assistant step actually ran. Aliases (`claude-code/opus`) resolve to a real id
   *  (e.g. `claude-opus-5-5`) reported by the backend; recorded so the transcript shows what truly ran. */
  model?: string;
}

/**
 * Providers whose ai.libx.js adapter sends `reasoning_content` back to the vendor: DeepSeek (thinking +
 * tools 400s without it) and Moonshot/Kimi ("preserve each historical assistant message's reasoning").
 * Everyone else ignores the field, so persisting it there would only bloat the transcript — cursor, for
 * one, streams its WHOLE reply as reasoning.
 */
export function passesBackReasoning(model: string | undefined): boolean {
  return !!model && /^(deepseek|moonshot)\//.test(model);
}

/** Flatten any message content to its text (string as-is; parts → concatenated text) — for length
 *  estimation, summaries, and display. Non-text parts (images) contribute a short placeholder. */
export function contentText(content: MessageContent | undefined): string {
  if (content == null) return '';
  if (typeof content === 'string') return content;
  return content.map((p) => (p.type === 'text' ? (p.text ?? '') : '[image]')).join(p_sep(content));
}
const p_sep = (parts: ContentPart[]): string => (parts.length > 1 ? '\n' : '');

/** Build an image content part from a data-URI or http(s) URL. */
export function imagePart(url: string): ContentPart {
  return { type: 'image_url', image_url: { url } };
}

/** Stub left when an inline data-URI image is elided from the stored transcript (RAM + disk). */
export const IMAGE_ELIDE_STUB = '[image elided — re-attach @path or /paste to view again]';

/** True when content carries an inline base64 image (the heavy case — http(s) URLs are tiny). */
export function messageHasInlineImage(content: MessageContent | undefined): boolean {
  if (content == null || typeof content === 'string') return false;
  return content.some((p) => p.type === 'image_url' && (p.image_url?.url ?? '').startsWith('data:'));
}

/** Byte-weight for context budgeting — counts base64 payloads, not the `[image]` display placeholder. */
export function contentBytes(content: MessageContent | undefined): number {
  if (content == null) return 0;
  if (typeof content === 'string') return content.length;
  let n = 0;
  for (const p of content) {
    if (p.type === 'text') { n += (p.text ?? '').length; continue; }
    const url = p.image_url?.url ?? '';
    const ref = parseImageRef(url);
    // A ref is ~60 chars on disk but becomes the whole file on the wire IF it survives the send-time
    // window. Callers that budget the real payload run `expandImagesForSend` FIRST (Agent.trimContext)
    // or use `sendBytes` — this is the honest weight for a still-unexpanded ref.
    n += (ref ? ref.bytes : url.length) || 7;
  }
  return n;
}

/** Replace inline data-URI image parts with a text stub; http(s) URLs, image REFS and plain text
 *  unchanged — a ref is ~60 bytes and stays re-expandable, so there is nothing to reclaim. */
export function elideInlineImages(content: MessageContent, stub = IMAGE_ELIDE_STUB): MessageContent {
  return elideImageParts(content, (url) => url.startsWith('data:'), stub);
}

/** Replace EVERY heavy image part — inline base64 AND refs — with the stub. This is the destructive
 *  form used by the `/compact` elision ladder: a ref is cheap on disk but weighs its whole file on the
 *  wire while it's inside the send window, so it must be shave-able or the ladder falls through to
 *  shredding (then dropping) the user's own text. `elideInlineImages` stays the non-destructive form. */
export function elideImages(content: MessageContent, stub = IMAGE_ELIDE_STUB): MessageContent {
  return elideImageParts(content, (url) => url.startsWith('data:') || isImageRefUrl(url), stub);
}

function elideImageParts(content: MessageContent, hit: (url: string) => boolean, stub: string): MessageContent {
  if (typeof content === 'string') return content;
  let changed = false;
  const out: ContentPart[] = [];
  for (const p of content) {
    if (p.type !== 'image_url' || !hit(p.image_url?.url ?? '')) { out.push(p); continue; }
    changed = true;
    out.push({ type: 'text', text: stub });
  }
  return changed ? out : content;
}

/**
 * A persist-safe REFERENCE to an image FILE. Stored in the transcript (and the session JSON) instead
 * of the megabytes of base64 — `expandImagesForSend` turns it back into a data-URI in the OUTGOING
 * request only, so neither the heap nor the disk ever holds historical payloads.
 *
 * SECURITY: a ref is a CAPABILITY, not a path. The URL carries an unguessable token; the real path
 * lives only in this in-process registry, and only HOST code that already did the jail/permission
 * check can mint one (`mintImageRef`, used by the CLI's @image attach and the image `Read` tool).
 * Tool results are MODEL-CONTROLLED text (bash prints whatever it likes) — parsing a path out of them
 * could never be the authorization. An unknown/evicted token is simply not a ref: it degrades to the
 * elision stub, never a file read. That also covers a resumed session in a fresh process.
 * Shape stays inside `image_url.url` so old readers/serializers need no schema change:
 *   `agentx-image:<mime>;<token>;<path>`
 * The trailing path is INERT BOOKKEEPING — `parseImageRef` never reads it, so it can't authorize
 * anything. It exists only so `remintImageRefs` can re-grant the capability on session load, after
 * re-checking the path against an allowed root (see there).
 */
export const IMAGE_REF_SCHEME = 'agentx-image:';

export interface ImageRef {
  mime: string;
  /** Absolute on-disk path. Never derived from model output — only from a mint-time capability. */
  path: string;
  /** base64 wire weight, stat'ed AT MINT TIME (so an overwritten-in-place path can never go stale). */
  bytes: number;
}

/** Bounded so a long session can't grow the registry without limit; oldest minted token is evicted
 *  first (and an evicted ref degrades to the stub, exactly like a missing file). */
const REF_REGISTRY_MAX = 256;
const refRegistry = new Map<string, ImageRef>();

/**
 * Mint an image-ref URL for an ALREADY-AUTHORIZED absolute path. Host-only: call this after the
 * jail/permission check, never on a path that came out of model output.
 */
export function mintImageRef(path: string, mime: string): string {
  const token = randomBytes(16).toString('hex');
  let bytes = 0;
  try { bytes = Math.ceil(statSync(path).size * 4 / 3); } // base64 expansion — what actually goes on the wire
  catch (e) { log.warn(`image ref stat failed, weighing as 0: ${path}`, e); }
  refRegistry.set(token, { mime, path, bytes });
  while (refRegistry.size > REF_REGISTRY_MAX) refRegistry.delete(refRegistry.keys().next().value as string);
  return `${IMAGE_REF_SCHEME}${mime};${token};${path}`;
}

/** Test/host hook: forget every minted ref (simulates a fresh process / resumed session). */
export function clearImageRefs(): void { refRegistry.clear(); }

/** Build an image REFERENCE part for an authorized path. */
export function imageRefPart(path: string, mime: string): ContentPart {
  return { type: 'image_url', image_url: { url: mintImageRef(path, mime) } };
}

/** Does this URL claim to be an image ref? (Claim only — resolution is `parseImageRef`.) */
const isImageRefUrl = (url: string | undefined): boolean => !!url && url.startsWith(IMAGE_REF_SCHEME);

/** Resolve an image-ref URL through the registry; null when it isn't one, or the token is unknown
 *  (forged by the model, evicted, or from a previous process). NEVER trusts the URL's own text. */
export function parseImageRef(url: string | undefined): ImageRef | null {
  const parts = refUrlParts(url);
  const ref = parts && refRegistry.get(parts.token);
  if (!ref) { log.debug('image ref not in registry — treating as non-image text'); return null; }
  return ref;
}

/** Split a ref URL into its literal fields. `path` here is UNTRUSTED bookkeeping — never a capability. */
function refUrlParts(url: string | undefined): { mime: string; token: string; path: string } | null {
  if (!isImageRefUrl(url)) return null;
  const rest = url!.slice(IMAGE_REF_SCHEME.length);
  const a = rest.indexOf(';');
  if (a < 0) return null;
  const b = rest.indexOf(';', a + 1);
  return { mime: rest.slice(0, a), token: b < 0 ? rest.slice(a + 1) : rest.slice(a + 1, b), path: b < 0 ? '' : rest.slice(b + 1) };
}

/**
 * Session LOAD: re-grant the capability for stored image parts whose token died with the previous
 * process, so a resumed session's recent screenshot is visible again (parity with the pre-ref
 * behaviour, where `elideStaleImages` kept the last inline image).
 *
 * A session file is WRITABLE BY THE MODEL (it can Write/Edit it, and the user may hand-edit it), so a
 * stored path is untrusted input — its provenance proves nothing. The ROOT CHECK is what makes this
 * safe: only a path whose REALPATH (symlinks resolved) sits inside one of `roots` is re-minted.
 * Anything else is left unresolvable and degrades to the elision stub, with a warn.
 *
 * Only image PARTS in user/assistant content are considered. Tool-result strings stay inert forever —
 * those are the model's own stdout and must never mint anything.
 * Pure view: returns a new array only when something changed.
 */
export function remintImageRefs(messages: Message[], roots: string[]): Message[] {
  const allowed: string[] = [];
  for (const r of roots) {
    try { allowed.push(realpathSync(resolve(r))); } catch (e) { log.debug(`image-ref root unavailable: ${r}`, e); }
  }
  if (allowed.length === 0) return messages;
  const inside = (p: string): boolean => allowed.some((root) => p === root || p.startsWith(root + sep));
  let changed = false;
  const out = messages.map((m) => {
    if ((m.role !== 'user' && m.role !== 'assistant') || typeof m.content === 'string' || m.content == null) return m;
    let touched = false;
    const parts = m.content.map((p) => {
      if (p.type !== 'image_url') return p;
      const f = refUrlParts(p.image_url?.url);
      if (!f || refRegistry.has(f.token)) return p; // not a ref, or still live in this process
      let real: string;
      try { real = realpathSync(f.path); } catch (e) { log.warn(`resumed image ref unreadable, will stub: ${f.path}`, e); return p; }
      if (!inside(real)) { log.warn(`resumed image ref outside the allowed roots, will stub: ${f.path} -> ${real}`); return p; }
      touched = true;
      return { type: 'image_url', image_url: { url: mintImageRef(real, f.mime) } } as ContentPart;
    });
    if (!touched) return m;
    changed = true;
    return { ...m, content: parts };
  });
  return changed ? out : messages;
}

/** True when content carries an image REF part (weightless on disk, expanded at send time). The
 *  CLAIM is enough: an unresolvable ref (resumed session / evicted token) still counts as image-bearing
 *  so the send path turns it into the stub instead of shipping the unresolvable scheme to the provider. */
export function messageHasImageRef(content: MessageContent | undefined): boolean {
  if (content == null || typeof content === 'string') return false;
  return content.some((p) => p.type === 'image_url' && isImageRefUrl(p.image_url?.url));
}

/** Any image at all — inline base64 (legacy sessions) or a ref. Drives `keepRecent` windows. */
export function messageHasImage(content: MessageContent | undefined): boolean {
  return messageHasInlineImage(content) || messageHasImageRef(content);
}

/** `Read` on an image persists this instead of the base64; expanded to `{dataUrl}` at send time.
 *  `path` must already have passed the filesystem's jail check (see `mintImageRef`). */
export function imageRefResult(path: string, mime: string): string {
  return JSON.stringify({ imageRef: mintImageRef(path, mime), image: path });
}

/** The registered ref behind a tool message's `{"imageRef":…}` JSON body, if that's what it is.
 *  A model-forged body parses fine but resolves to null — the token isn't in the registry. */
export function toolImageRef(content: MessageContent | undefined): ImageRef | null {
  if (typeof content !== 'string' || !content.startsWith('{"imageRef":')) return null;
  try { return parseImageRef(JSON.parse(content).imageRef); } catch (e) { log.debug('bad imageRef tool result', e); return null; }
}

/** Elide inline images from all but the most-recent `keepRecent` image-bearing messages (mutates copies). */
export function elideStaleImages(messages: Message[], keepRecent = 1): Message[] {
  const idx = messages.map((m, i) => (messageHasImage(m.content) ? i : -1)).filter((i) => i >= 0);
  const drop = new Set(idx.slice(0, Math.max(0, idx.length - keepRecent)));
  if (drop.size === 0) return messages;
  let changed = false;
  const out = messages.map((m, i) => {
    if (!drop.has(i)) return m;
    const c = elideInlineImages(m.content);
    if (c === m.content) return m;
    changed = true;
    return { ...m, content: c };
  });
  return changed ? out : messages;
}

/**
 * The send-time verdict per message index. User ATTACHMENTS and tool-result image `Read`s get
 * INDEPENDENT windows: an image the model Reads mid-turn must never evict the image the user just
 * pasted (the spec's non-goal — vision on the paste turn must keep working), and vice-versa.
 */
function imageWindows(messages: Message[], keepRecent: number): Set<number> {
  const attach: number[] = [];
  const tool: number[] = [];
  messages.forEach((m, i) => {
    if (toolImageRef(m.content)) tool.push(i);
    else if (messageHasImage(m.content)) attach.push(i);
  });
  const k = Math.max(0, keepRecent);
  return new Set([...attach.slice(Math.max(0, attach.length - k)), ...tool.slice(Math.max(0, tool.length - k))]);
}

/** Is this message image-bearing at all (either kind)? */
const bearsImage = (m: Message): boolean => messageHasImage(m.content) || toolImageRef(m.content) != null;

/**
 * SEND-PATH expansion: image REFS become real data-URIs only in the outgoing request, and only for the
 * most-recent `keepRecent` messages of EACH kind (user attachment / tool image Read) — older ones (refs
 * AND legacy inline base64) collapse to the elision stub. Pure view: the caller's messages are never mutated.
 * A missing/unreadable file (or an unregistered ref) degrades to the same stub and is LOGGED — never throws.
 */
export function expandImagesForSend(messages: Message[], keepRecent = 1, stub = IMAGE_ELIDE_STUB): Message[] {
  if (!messages.some(bearsImage)) return messages;
  const keep = imageWindows(messages, keepRecent);
  let changed = false;
  const out = messages.map((m, i) => {
    const content = keep.has(i) ? expandContent(m.content, stub) : stripContent(m.content, stub);
    if (content === m.content) return m;
    changed = true;
    return { ...m, content };
  });
  return changed ? out : messages;
}

/**
 * Byte-weight of a transcript AS IT WILL GO ON THE WIRE — refs inside the keep window weigh their
 * file's base64 size, everything stale weighs the stub — WITHOUT materializing a single byte of
 * base64. This is the honest number for `/context` and the CLI footer, where the old behaviour made
 * every historical screenshot immortal and produced false "90% full" warnings.
 */
export function sendBytes(messages: Message[], keepRecent = 1, stub = IMAGE_ELIDE_STUB): number {
  let n = 0;
  for (const b of sendBytesPer(messages, keepRecent, stub)) n += b;
  return n;
}

/** `sendBytes` split per message — same window, same weights. Lets a budgeter drop/shave message-by-
 *  message without re-deriving the window (and without charging a stale ref its file weight). */
export function sendBytesPer(messages: Message[], keepRecent = 1, stub = IMAGE_ELIDE_STUB): number[] {
  const keep = messages.some(bearsImage) ? imageWindows(messages, keepRecent) : null;
  return messages.map((m, i) =>
    wireBytes(m.content, keep == null || keep.has(i), stub) + (m.tool_calls ? JSON.stringify(m.tool_calls).length : 0) + (m.reasoning_content?.length ?? 0));
}

/** contentBytes, but aware of whether this message's images survive the send-time window. */
function wireBytes(content: MessageContent | undefined, kept: boolean, stub: string): number {
  const tool = toolImageRef(content);
  if (tool) return kept ? tool.bytes : stub.length;
  if (content == null || typeof content === 'string') return contentBytes(content);
  if (kept) return contentBytes(content);
  let n = 0;
  for (const p of content) {
    if (p.type === 'text') { n += (p.text ?? '').length; continue; }
    const url = p.image_url?.url ?? '';
    n += url.startsWith('data:') || isImageRefUrl(url) ? stub.length : url.length || 7;
  }
  return n;
}

/** Read a referenced image as a data-URI; null (logged) when the file is gone/unreadable. */
function readRefDataUrl(ref: ImageRef): string | null {
  try { return `data:${ref.mime};base64,${readFileSync(ref.path).toString('base64')}`; }
  catch (e) { log.warn(`image ref unreadable, sending stub instead: ${ref.path}`, e); return null; }
}

/** Refs → data-URIs (tool `{imageRef}` bodies → the `{dataUrl}` shape the wire adapter renders). */
function expandContent(content: MessageContent, stub: string): MessageContent {
  const tool = toolImageRef(content);
  if (tool) {
    const url = readRefDataUrl(tool);
    return url ? JSON.stringify({ dataUrl: url, image: tool.path }) : stub;
  }
  if (typeof content === 'string') return content;
  let changed = false;
  const out = content.map((p) => {
    if (p.type !== 'image_url' || !isImageRefUrl(p.image_url?.url)) return p;
    changed = true;
    // Unresolvable (resumed session / evicted token) → stub. Content PARTS are host-authored, so an
    // unknown one is a lost capability, not model text — and the scheme must never reach the provider.
    const ref = parseImageRef(p.image_url?.url);
    const url = ref && readRefDataUrl(ref);
    return url ? imagePart(url) : ({ type: 'text', text: stub } as ContentPart);
  });
  return changed ? out : content;
}

/** Stale image-bearing content as it goes on the wire: no base64, no ref the provider can't resolve. */
function stripContent(content: MessageContent, stub: string): MessageContent {
  if (toolImageRef(content)) return stub;
  if (typeof content === 'string') return content;
  let changed = false;
  const out = content.map((p) => {
    const url = p.type === 'image_url' ? (p.image_url?.url ?? '') : '';
    if (!url.startsWith('data:') && !isImageRefUrl(url)) return p;
    changed = true;
    return { type: 'text', text: stub } as ContentPart;
  });
  return changed ? out : content;
}

export interface Tool {
  type: 'function';
  function: { name: string; description?: string; parameters: object };
}

export interface ChatResponse {
  content: string;
  finishReason?: string;
  toolCalls?: ToolCall[];
  model?: string;
  usage?: { promptTokens: number; completionTokens: number; totalTokens: number };
  /** The step's accumulated reasoning/thinking text (provider-dependent). */
  reasoningContent?: string;
  /** Tools a self-executing runtime (cursor/claude-code) ran inside its OWN loop — correlated from
   *  the display-only `toolActivity` stream. Not dispatched by us; persisted so the transcript keeps
   *  a faithful record (audit + resume) instead of being narration-only. */
  delegatedTools?: { id: string; name: string; input?: unknown; output?: unknown; status: 'running' | 'completed' | 'error' }[];
  /** A delegated runtime (cursor/claude-code) ran tools this turn but never CLOSED it: either no assistant
   *  text followed the last tool (it went silent post-work), or the text it did emit ends on an action it
   *  announced and never took. Signals the loop's closure reflex (see AgentOptions.closeDelegatedTurns). */
  endedWithoutClosing?: boolean;
}

/**
 * Does this assistant text end mid-plan — on an action the model ANNOUNCED but never took?
 *
 * Cursor/composer narrates as it works ("Searching the codebase…", "Also checking for X"), and its run
 * can settle FINISHED while the narration is still mid-plan. The turn then looks closed (finishReason
 * 'stop', text present) but delivered no answer. Measured over the local cursor session corpus: ~4.6%
 * of tool-using turns end this way.
 *
 * Matches only FIRST-PERSON commitments ("Let me…", "I'll…", "I need to…") and progressive narration
 * ("Checking…", "Expanding…"). Deliberately excludes "Next step: …" / "Next run will…" and "Let me know
 * if…" — those are legitimate things to say TO the user, and nudging them would talk over a finished
 * answer. A false positive costs one extra step, so the rule stays conservative rather than clever.
 *
 * The two arms are deliberately scoped differently:
 *
 * COMMITMENT — first-person SINGULAR "I'll / let me / I'm going to", plus the one obligation form the
 * corpus actually stalled on: "I need to…". These carry their own subject and tense, so they stay
 * unambiguous anywhere in the sentence and may be matched clause-by-clause — the observed stuck turn
 * hid its commitment in a trailing "…, so I need to verify …" clause. Past-tense reports of the same
 * ("I needed to") are excluded: they describe work already reasoned about, not work still pending.
 * Second person ("you'll need to…") is excluded — that is a recommendation to the user.
 *
 * Deliberately NOT matched: "we …" and the softer obligations "should / must / have to / want to".
 * They read as recommendations TO the user on a FINISHED answer ("All tests pass. We should be good to
 * go.", "I have to admit, the numbers were surprising."), the same category as the NEXT_STEP and
 * SIGN_OFF carve-outs — and nudging a finished answer spends a metered step talking over it. No turn in
 * the corpus needed them, so the conservative bias keeps them out.
 *
 * PROGRESSIVE — a bare gerund ("Checking the images…") is only a commitment at the HEAD of the final
 * sentence. Mid-sentence it is almost always a participial modifier of a COMPLETED action
 * ("I pruned the cache, reclaiming 2.6 GB"), so it is not matched clause-wise.
 */
const COMMITMENT =
  /^(?:also\s+|now\s+|then\s+|so\s+)*(?:let me\b(?!\s+know\b)|i'?ll\b|i will\b|i'?m going to\b|i\s+(?:still\s+|now\s+|first\s+)*need to\b)/i;
const PROGRESSIVE =
  /^(?:also\s+|now\s+|then\s+)*(?:re-?)?(?:check|search|scan|read|verify|run|inspect|expand|look|examin|continu|proceed|try|fetch|load|open|test|build|grep|find|review|trac|dig|explor)\w*ing\b/i;
const NEXT_STEP = /^next (?:step|run|up)\b/i;
// "Let me know if…" is a SIGN-OFF, not an announced action. It only ever reached the `let me\b` arm on
// delegated turns before; once the sawTool gate is dropped (announce-and-stop turns run no tools at all)
// it would fire on every polite closing line, so exclude it explicitly (in COMMITMENT and here).
const SIGN_OFF = /^let me know\b/i;

/** Split a sentence into clauses at punctuation and coordinators, so a trailing commitment is visible. */
function clauses(sentence: string): string[] {
  return sentence
    .split(/[,;:]\s*|\s+(?:so|but|and|then|because|however|therefore|thus|although|though)\s+/i)
    .map((c) => c.trim())
    .filter(Boolean);
}

export function endsOnAnnouncedAction(text: string): boolean {
  const sentences = text.split(/(?<=[.!?])\s+|\n+/).map((s) => s.trim()).filter(Boolean);
  const last = sentences[sentences.length - 1];
  if (!last || NEXT_STEP.test(last) || SIGN_OFF.test(last)) return false;
  if (PROGRESSIVE.test(last)) return true;
  return clauses(last).some((c) => !NEXT_STEP.test(c) && !SIGN_OFF.test(c) && COMMITMENT.test(c));
}

/**
 * One incremental event from a streamed `chat({stream:true})` call — mirrors
 * ai.libx.js's `StreamChunk` (OpenAI-style): each chunk carries a `content`
 * text delta; the terminal chunk carries `finishReason` and the accumulated
 * `toolCalls`. Consuming the stream and folding the deltas reconstructs the
 * same `ChatResponse` the non-stream path returns.
 */
export interface StreamChunk {
  content: string;
  finishReason?: string;
  index?: number;
  /** Concrete model the backend actually ran, when it differs from the requested alias (e.g. a
   *  `claude-code/opus` request runs `claude-opus-5-5`). Mirrors ai.libx.js's StreamChunk.model. */
  model?: string;
  toolCalls?: ToolCall[]; // accumulated tool calls (typically on the final chunk)
  reasoningContent?: string;
  /** Display-only tool activity from a self-executing backend (Cursor CLI) — surfaced as a host
   *  `tool_activity` event, never dispatched. Mirrors ai.libx.js's StreamChunk.toolActivity. */
  toolActivity?: { id?: string; name: string; input?: unknown; output?: unknown; status: 'running' | 'completed' | 'error'; truncated?: { args?: boolean; result?: boolean } };
  usage?: { promptTokens: number; completionTokens: number; totalTokens: number }; // on the terminal chunk, when the provider reports it
}

export interface ChatOptions {
  model: string;
  messages: Message[];
  tools?: Tool[];
  toolChoice?: unknown;
  stream?: boolean;
  /** Cancel the request/stream. Forwarded to providers that honor it; the Agent also stops consuming on abort. */
  signal?: AbortSignal;
  [k: string]: unknown;
}

/** Minimal shape of an ai.libx.js AIClient that the Agent drives. */
export interface ChatLike {
  chat(options: ChatOptions): Promise<ChatResponse | AsyncIterable<StreamChunk>>;
}
